import { Input, Select } from "antd";
import Title from "antd/es/typography/Title";
import React, { FC, useEffect, useState } from "react";
import {
  ActiveDynamicTrackerFieldFilter,
  DynamicTrackerFieldOperation,
} from "shared/types";

export const DYNAMIC_FIELD_OPERATION_OPTIONS: {
  label: string;
  value: DynamicTrackerFieldOperation;
}[] = [
  { label: "= (eq)", value: "eq" },
  { label: "≥ (gte)", value: "gte" },
  { label: "≤ (lte)", value: "lte" },
];

interface DynamicColumnFilterDropdownProps {
  section: string;
  field: string;
  // The single active dynamic filter, shared across the whole table.
  activeFilter: ActiveDynamicTrackerFieldFilter | null;
  visible: boolean;
  onApply: (filter: ActiveDynamicTrackerFieldFilter) => void;
  onClear: () => void;
}

const DynamicColumnFilterDropdown: FC<DynamicColumnFilterDropdownProps> = ({
  section,
  field,
  activeFilter,
  visible,
  onApply,
  onClear,
}) => {
  const isActiveColumn =
    activeFilter?.section === section && activeFilter?.field === field;

  const [operation, setOperation] =
    useState<DynamicTrackerFieldOperation>("eq");
  const [value, setValue] = useState("");

  // Sync local editing state with the shared filter whenever the dropdown opens.
  useEffect(() => {
    if (!visible) return;
    setOperation(isActiveColumn ? activeFilter!.operation : "eq");
    setValue(isActiveColumn ? activeFilter!.value : "");
  }, [visible]);

  // Apply live: a non-empty value activates the filter on this column, an empty
  // value clears it (but only if this column was the active one, so changing the
  // operator of an empty dropdown doesn't wipe another column's filter).
  const commit = (op: DynamicTrackerFieldOperation, rawValue: string) => {
    const trimmed = rawValue.trim();
    if (trimmed) {
      onApply({ section, field, operation: op, value: trimmed });
    } else if (isActiveColumn) {
      onClear();
    }
  };

  const handleOperationChange = (op: DynamicTrackerFieldOperation) => {
    setOperation(op);
    commit(op, value);
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    setValue(nextValue);
    commit(operation, nextValue);
  };

  return (
    <div className="flex-col p-3 w-[240px]">
      <Title level={5}>{`${section}.${field}`}</Title>

      <div className="flex flex-col gap-2">
        <Select
          options={DYNAMIC_FIELD_OPERATION_OPTIONS}
          value={operation}
          onChange={handleOperationChange}
          className="w-full"
        />
        <Input
          placeholder="Value"
          value={value}
          onChange={handleValueChange}
          allowClear
        />
      </div>
    </div>
  );
};

export { DynamicColumnFilterDropdown };
